import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

export type BreadcrumbItem = {
  label: string
  href?: string
}

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[]
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextType | null>(null)

function areBreadcrumbsEqual(a: BreadcrumbItem[], b: BreadcrumbItem[]) {
  if (a.length !== b.length) return false

  return a.every((item, index) => {
    const other = b[index]
    return item.label === other.label && item.href === other.href
  })
}

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>([])

  const setBreadcrumbs = useCallback((newBreadcrumbs: BreadcrumbItem[]) => {
    setBreadcrumbsState((currentBreadcrumbs) => (
      areBreadcrumbsEqual(currentBreadcrumbs, newBreadcrumbs) ? currentBreadcrumbs : newBreadcrumbs
    ))
  }, [])

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

export function useBreadcrumbs() {
  const context = useContext(BreadcrumbContext)
  if (!context) {
    throw new Error('useBreadcrumbs must be used within BreadcrumbProvider')
  }
  return context
}
